import { ForwardedRef, forwardRef, useMemo } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { TextField } from './text-field';
import { TextFieldRhfProps } from './text-field.types';

type FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

/**
 * TextFieldRhf is a React component that integrates the TextField with React Hook Form.
 * It uses the Controller component to manage the form state and validation.
 *
 * @param props - The properties for the TextFieldRhf component.
 * @returns A TextField component wrapped with React Hook Form's Controller.
 */
export const TextFieldRhf = forwardRef(
  (props: TextFieldRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { name, rules } = props;
    const { control } = useFormContext();
    const renderField = useMemo(() => fieldFactory(props, ref), [props, ref]);

    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        render={renderField}
      />
    );
  },
);

/**
 * In order to be able to use the `useMergeRefs`, had to extract the component render.
 * This should ensure correctness of the of the react-hook-form integration.
 **/
const fieldFactory = (props: TextFieldRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
  return ({ field, fieldState }: FieldProps) => {
    const mergedRefs = useMergeRefs([field.ref, ref]);
    const { rules, hidden, ...textFieldProps } = props;
    return (
      <TextField
        {...textFieldProps}
        hidden={hidden}
        required={!!rules?.required}
        ref={mergedRefs}
        onChange={(e) => {
          field.onChange(e);
          textFieldProps.onChange?.(e);
        }}
        onBlur={(e) => {
          field.onBlur();
          textFieldProps.onBlur?.(e);
        }}
        value={field.value ?? ''}
        errorMessage={fieldState.error?.message?.toString()}
      />
    );
  };
};
